import ProjectOverview from "./ProjectOverview";
import { isLeakDetectionProject } from "../repository/ProjectRepository";
import LeakDetectionProjectOverview from "./LeakDetectionProjectOverview";
import { DryingProjectOverview } from "./DryingProjectOverview";
import { DateTime } from "luxon";
import { filterOutUndefined } from "@/utils/Utils";
import { ALL_PROJECT_TYPES, ExtendedProjectType, isPortalOnlyProjectTypeId, isRepairOnlyProjectTypeId } from "./ProjectDetails";

export interface ProjectStepsContainer {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectAccepted?: any;                              // Step 1
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectApproved?: any;                              // Step 2
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  firstContact?: any;                                 // Step 3
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  leakDetectionAppointment?: any;                     // Step 4
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  leakDetectionResult?: any;                          // Step 5
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  damageAssessmentAppointment?: any;                  // Step 6
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  damageAssessmentResult?: any;                       // Step 7
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  offerSendToInsurance?: any;                         // Step 8
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  offerResult?: any;                                  // Step 9

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dryingSetupAppointments?: any[];                    // Step 10
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dryingSetupCompletions?: any[];                     // Step 11

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intermediateMeasurementAppointments?: any[];        // Step 12
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intermediateMeasurementCompletions?: any[];         // Step 13

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dryingConclusionAppointments?: any[];               // Step 14
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dryingConclusionResults?: any[];                    // Step 15
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dryingConclusionResult?: any;                       // Step 15 (flattened)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  repairAppointments?: any[];                         // Step 16
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  repairStart?: any;                                  // Step 17
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectResult?: any;                                // Step 18
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getStepCreationTimestamp(step: any) {
  if (!step) {
    return null;
  }

  if (Array.isArray(step)) {
    let maxDate = -1;

    for (const entry of step) {
      const creationDate = getStepCreationTimestamp(entry);

      if (creationDate && creationDate > maxDate) {
        maxDate = creationDate;
      }
    }

    return maxDate >= 0 ? maxDate : undefined;
  }

  const creationTimestamp = step.creationTimestamp;

  if (!creationTimestamp) {
    return null;
  }

  return DateTime.fromISO(creationTimestamp)?.toMillis();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getStepCreationDate(step: any): Date | null {
  if (!step) {
    return null;
  }

  if (Array.isArray(step)) {
    let maxDate: Date | null = null;

    for (const entry of step) {
      const creationDate = getStepCreationDate(entry);

      if (
        creationDate &&
        (!maxDate || creationDate.getTime() > maxDate.getTime())
      ) {
        maxDate = creationDate;
      }
    }

    return maxDate;
  }

  const creationTimestamp = step.creationTimestamp;

  if (!creationTimestamp) {
    return null;
  }

  return DateTime.fromISO(creationTimestamp)?.toJSDate() ?? null;
}

/**
 * Checks whether a project step has been started, i.e. at least one entry
 * has a non-empty `creationTimestamp`. Handles both single-object steps and
 * contact-person array steps (e.g. steps 10, 14).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function hasStepStarted(step: any): boolean {
  if (!step) return false;
  if (Array.isArray(step)) {
    return step.some((s) => !!s.creationTimestamp);
  }
  return !!step.creationTimestamp;
}

export function getProjectStepContainer(
  projectOverview: ProjectOverview
): ProjectStepsContainer | undefined {
  if (isLeakDetectionProject(projectOverview.id)) {
    return (projectOverview as LeakDetectionProjectOverview)
      ?.leakDetectionProjectSteps;
  } else {
    return (projectOverview as DryingProjectOverview)?.dryingProjectSteps;
  }
}

export function getEstimatedDryingTimeInDays(
  projectSteps: ProjectStepsContainer
) {
  return projectSteps.offerResult?.estimatedDryingTime
    ?? projectSteps.damageAssessmentResult?.estimatedDryingTime
    ?? null;
}

function flattenArrayIfNeeded(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  step: any
) {
  return Array.isArray(step) ? step[0] : step
}

export function createContainerForProjectSteps(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectSteps: any
): ProjectStepsContainer {

  const step11 = projectSteps["11"]
  const dryingSetupCompletions = step11 ? (Array.isArray(step11) ? step11  : [step11]) : undefined

  const step13 = projectSteps["13"]
  const intermediateMeasurementCompletions = step13 ? (Array.isArray(step13) ? step13  : [step13]) : undefined

  return filterOutUndefined({
    projectAccepted: projectSteps["1"],
    projectApproved: projectSteps["2"],
    firstContact: projectSteps["3"],
    leakDetectionAppointment: projectSteps["4"],
    leakDetectionResult: projectSteps["5"],
    damageAssessmentAppointment: projectSteps["6"],
    damageAssessmentResult: projectSteps["7"],
    offerSendToInsurance: projectSteps["8"],
    offerResult: projectSteps["9"],

    dryingSetupAppointments: projectSteps["10"],
    dryingSetupCompletions: dryingSetupCompletions,

    intermediateMeasurementAppointments: projectSteps["12"],
    intermediateMeasurementCompletions: intermediateMeasurementCompletions,

    dryingConclusionAppointments: projectSteps["14"],
    dryingConclusionResults: projectSteps["15"],
    dryingConclusionResult: flattenArrayIfNeeded(projectSteps["15"]),

    repairAppointments: projectSteps["16"],
    repairStart: flattenArrayIfNeeded(projectSteps["17"]),

    projectResult: flattenArrayIfNeeded(projectSteps["18"]),
  });
}

/**
 * Builds and returns a map of project step dependencies based on the given project type.
 *
 * Each entry in the map has the step number as the key and an array of step numbers
 * that may unlock this step. Fulfilling **any one** of the listed prerequisites is
 * sufficient to proceed to the step. The exact dependencies vary depending
 * on the project type scope (e.g., leak detection, drying, restoration).
 *
 * @param {number} projectTypeId - The unique identifier of the project type.
 * @returns {Map<number, number[]>} A map where keys are step numbers and values are arrays of prerequisite step numbers.
 *
 * Behavior:
 * - Steps 1 and 2 are always included (with 2 depending on 1).
 * - Step 3 is only added for non-portal-only projects.
 * - Leak detection flow introduces steps 4 and 5.
 * - Damage assessment flow introduces steps 6 and 7.
 * - Insurance offer flow manages steps 8 and (or project worker assignment for potal ony projects) 9.
 * - Drying flow covers steps 10 to 15.
 * - Restoration flow covers steps 16 and 17.
 * - Project conclusion (step 18) depends on restoration or drying completion.
 *
 * If the project type is unknown, returns an empty map.
 */
export function getStepDependencies(projectTypeId: number): Map<number, number[]> {
  const projectTypes = ALL_PROJECT_TYPES;
  const currentProjectType = projectTypes.find((pt) => pt.projectTypeId === projectTypeId);

  // If the project type isn't known, preserve previous behavior and return an empty array
  if (!currentProjectType) {
    return new Map<number, number[]>();
  }

  const stepDependencies = new Map<number, number[]>([
    [1, []],
    [2, [1]],
  ]);

  const scope = currentProjectType.projectScope;
  const isPortalOnlyProject = isPortalOnlyProjectTypeId(projectTypeId);

  const hasLeakDetection = scope.includes(ExtendedProjectType.LEAK_DETECTION);
  const hasDrying = scope.includes(ExtendedProjectType.DRYING);
  const hasRestoration = scope.includes(ExtendedProjectType.RESTORATION);
  const hasOnlyRestoration = isRepairOnlyProjectTypeId(projectTypeId);
  const hasDamageAssessment =
    scope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING) ||
    scope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_RESTORATION) ||
    scope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_DRYING_RESTORATION) ||
    scope.includes(ExtendedProjectType.DAMAGE_ASSESSMENT_FIRE);
  const hasNonLeakDetectionScope = scope.some(
    (s) => s !== ExtendedProjectType.LEAK_DETECTION
  );

  // Step 3 only for non-portal-only projects
  if (!isPortalOnlyProject) {
    stepDependencies.set(3, [2]);
  }

  // Leak detection flow (4 -> 5)
  if (hasLeakDetection) {
    stepDependencies.set(4, [2, 3]);
    stepDependencies.set(5, [4]);
  }

  // Damage assessment flow (6 -> 7)
  if (hasDamageAssessment) {
    stepDependencies.set(6, [2, 3]);
    stepDependencies.set(7, [6]);
  }

  // Project worker assignment (9)
  if (isPortalOnlyProject) {
    stepDependencies.set(9, [2]);
  }
  // Insurance offer flow (8 -> 9)
  else if (hasNonLeakDetectionScope) {
    stepDependencies.set(8, [7]);
    stepDependencies.set(9, [7, 8]);
  }

  // Drying flow (10 -> 15)
  if (isPortalOnlyProject) {
    stepDependencies.set(10, [2, 9]);
    stepDependencies.set(11, [10]);
    stepDependencies.set(12, [11]);
    stepDependencies.set(13, [12]);
    stepDependencies.set(14, [11]);
    stepDependencies.set(15, [14]);
  }
  else {
    if (hasDrying) {
      stepDependencies.set(10, [7, 8, 9]);
      stepDependencies.set(11, [10]);
      stepDependencies.set(12, [11]);
      stepDependencies.set(13, [12]);
      stepDependencies.set(14, [11, 13]);
      stepDependencies.set(15, [14]);
    }
  }

  // Restoration flow (16 -> 17)
  if (hasRestoration) {
    // ASSUMPTION: Restoration can't be done without damage assessment. 
    stepDependencies.set(16, hasDrying ? [15] : [7, 8, 9]);
    stepDependencies.set(17, [16]);
  }

  if (hasNonLeakDetectionScope) {
    stepDependencies.set(18, hasOnlyRestoration ? [17] : [15]);
  }

  return stepDependencies;
}